import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export default function ManageAttendance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(1);
  const [savingSessionId, setSavingSessionId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  
  const initialAttendanceSessions = [
    {
      id: 1,
      week: 1,
      title: 'Week 1 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSeKjcqNMyIEbppSjnsW3dEX4ZR0MHqR2gp0esowm3jIT0fOPA/viewform?usp=sharing&ouid=108026927187778950974',
      status: 'open'
    },
    {
      id: 2,
      week: 2,
      title: 'Week 2 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSeAAxaR6h3iYCk9Qrtqb3YO7o4tgynFRoyIhAk3OPlBsSBg0Q/viewform?usp=sharing&ouid=108026927187778950974',
      status: 'closed'
    },
    {
      id: 3,
      week: 3,
      title: 'Week 3 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLScz9d2iKfLFdpMgrdXcqLKLojYId3kmMcczogDWy4SVmEJMYw/viewform?usp=publish-editor',
      status: 'closed'
    },
    {
      id: 4,
      week: 4,
      title: 'Week 4 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSfeXZ3kbMei6DHbC2AgZJh1JZHCwJqG5hWyzVAP_5s6tGfjvA/viewform?usp=publish-editor',
      status: 'closed'
    },
    {
      id: 5,
      week: 5,
      title: 'Week 5 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSe4fsPEShb8_EOthpCo-Ylg5ygHVyKPnSL4puxCHOm29Y7Smg/viewform?usp=sharing&ouid=108026927187778950974',
      status: 'closed'
    },
    {
      id: 6,
      week: 6,
      title: 'Week 6 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSeAPnzzIvqj8Vhaw-e62etZl8ds0UruwRHL54CfH8VwCdrOZQ/viewform?usp=publish-editor',
      status: 'closed'
    },
    {
      id: 7,
      week: 7,
      title: 'Week 7 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSfD_CkrlRo2ut2iNLGJePLTagVJ632NTaTrQGWmIlKmElJS5A/viewform?usp=publish-editor',
      status: 'closed'
    },
    {
      id: 8,
      week: 8,
      title: 'Week 8 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSd0zeOoZ3zKWN4eJHL1Ixbco3ch5HEcfOX235_yadQe66vaaQ/viewform?usp=publish-editor',
      status: 'closed'
    },
    {
      id: 9,
      week: 9,
      title: 'Week 9 Attendance',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLScLIxZK5Gr9Zqm3di5AR9wJWC69eja0A8CLzWMpMWBgzFYq9w/viewform?usp=publish-editor',
      status: 'closed'
    }
  ];
  
  const [attendanceSessions, setAttendanceSessions] = useState(initialAttendanceSessions);

  // Load attendance statuses from Firestore on mount
  useEffect(() => {
    const loadAttendanceStatuses = async () => {
      if (!user || !user.id) {
        console.log('User not available yet');
        return;
      }
      
      try {
        console.log('Loading attendance statuses for user:', user.id);
        const statusRef = doc(db, 'instructors', user.id, 'attendanceStatuses', 'statuses');
        const statusDoc = await getDoc(statusRef);
        
        if (statusDoc.exists()) {
          const savedStatuses = statusDoc.data();
          console.log('Found saved statuses:', savedStatuses);
          // Update attendance sessions with saved statuses from Firestore
          setAttendanceSessions(prevSessions => 
            prevSessions.map(session => ({
              ...session,
              status: savedStatuses[`attendance_${session.id}`] || session.status
            }))
          );
        } else {
          console.log('No saved statuses found in Firestore');
        }
      } catch (error) {
        console.error('Error loading attendance statuses from Firestore:', error);
      }
    };

    loadAttendanceStatuses();
  }, [user]);

  const weeks = [...new Set(attendanceSessions.map(a => a.week))].sort((a, b) => a - b);

  const toggleAttendanceStatus = async (id) => {
    setSavingSessionId(id);
    const session = attendanceSessions.find(a => a.id === id);
    
    let updatedSessions;
    
    // If opening a session, close all others
    // If closing a session, just close it
    if (session.status === 'closed') {
      updatedSessions = attendanceSessions.map(a =>
        a.id === id ? { ...a, status: 'open' } : { ...a, status: 'closed' }
      );
    } else {
      updatedSessions = attendanceSessions.map(a =>
        a.id === id ? { ...a, status: 'closed' } : a
      );
    }
    
    setAttendanceSessions(updatedSessions);
    const updatedSession = updatedSessions.find(a => a.id === id);
    
    console.log('Toggling attendance:', id, 'New status:', updatedSession.status);
    console.log('User ID:', user?.id);

    // Save to Firestore
    if (user && user.id) {
      try {
        // Ensure the parent instructor document exists - this is critical for student queries
        const instructorRef = doc(db, 'instructors', user.id);
        await setDoc(instructorRef, { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, { merge: true });
        
        const statusRef = doc(db, 'instructors', user.id, 'attendanceStatuses', 'statuses');
        console.log('Saving to Firestore path:', `instructors/${user.id}/attendanceStatuses/statuses`);
        
        // Get existing statuses first
        const statusDoc = await getDoc(statusRef);
        const existingStatuses = statusDoc.exists() ? statusDoc.data() : {};
        
        const updateData = {
          ...existingStatuses
        };
        
        // Update all sessions' statuses
        updatedSessions.forEach(s => {
          updateData[`attendance_${s.id}`] = s.status;
        });
        updateData.lastUpdated = new Date().toISOString();
        
        console.log('Saving data:', updateData);
        
        // Update with new status
        await setDoc(statusRef, updateData);
        console.log('Successfully saved attendance status:', updatedSession.status);
        
        // Show success message
        setStatusMessage({
          type: 'success',
          title: `${updatedSession.title} ${updatedSession.status === 'open' ? 'Opened' : 'Closed'}`,
          message: `Attendance form is now ${updatedSession.status}`
        });
        
        setTimeout(() => setStatusMessage(null), 3000);
        
      } catch (error) {
        console.error('Error saving attendance status to Firestore:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        // Show error message
        setStatusMessage({
          type: 'error',
          title: 'Save Failed',
          message: error.message || 'Could not save attendance status'
        });
        
        // Revert the change if save fails
        setAttendanceSessions(attendanceSessions);
        setTimeout(() => setStatusMessage(null), 4000);
      } finally {
        setSavingSessionId(null);
      }
    } else {
      console.error('User not authenticated or ID not available');
      setStatusMessage({
        type: 'error',
        title: 'Not Authenticated',
        message: 'Please log in as an instructor'
      });
      setSavingSessionId(null);
      setAttendanceSessions(attendanceSessions);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  // Get attendance for current week
  const currentAttendance = attendanceSessions.find(a => a.week === currentWeek);

  return (
    <div style={{ background: '#ffffff', minHeight: 'calc(100vh - 70px)', padding: '3rem 2rem' }}>
      {/* Status Message Modal */}
      {statusMessage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: statusMessage.type === 'success' ? '#e8f5e9' : '#ffebee',
          border: `2px solid ${statusMessage.type === 'success' ? '#4caf50' : '#f44336'}`,
          borderRadius: '8px',
          padding: '1.5rem',
          maxWidth: '400px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideInBottom 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ fontSize: '1.5rem' }}>
              {statusMessage.type === 'success' ? '✓' : '✕'}
            </div>
            <div>
              <h4 style={{ 
                margin: '0 0 0.5rem 0', 
                color: statusMessage.type === 'success' ? '#2e7d32' : '#c62828',
                fontWeight: 700
              }}>
                {statusMessage.title}
              </h4>
              <p style={{ margin: 0, color: statusMessage.type === 'success' ? '#1b5e20' : '#b71c1c', fontSize: '0.95rem' }}>
                {statusMessage.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideInBottom {
          from {
            transform: translateY(400px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem', display: 'flex', alignItems: 'center', gap: '1rem', paddingBottom: '1.5rem', borderBottom: '2px solid #e8e8e8' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#1a1a1a',
              border: 'none',
              color: 'white',
              padding: '0.6rem 0.8rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 300ms ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#2d2d2d';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#1a1a1a';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1a1a1a', letterSpacing: '-1px', margin: '0' }}>
              <i className="fas fa-clipboard-list" style={{ marginRight: '1rem', color: '#1a1a1a' }}></i>
              Manage Attendance
            </h1>
            <p style={{ fontSize: '1rem', color: '#666', margin: '0.5rem 0 0 0' }}>
              Open, close, and manage weekly attendance forms for students
            </p>
          </div>
        </div>

        {/* Week Selector */}
        <div style={{ marginBottom: '3rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem' }}>Select Week:</label>
          <select 
            value={currentWeek} 
            onChange={(e) => setCurrentWeek(parseInt(e.target.value))}
            style={{
              padding: '0.75rem 1rem',
              border: '1px solid #e8e8e8',
              borderRadius: '6px',
              fontFamily: 'inherit',
              fontSize: '1rem',
              background: 'white',
              color: '#1a1a1a',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 300ms ease'
            }}
          >
            {weeks.map(week => (
              <option key={week} value={week}>Week {week}</option>
            ))}
          </select>
        </div>

        {/* Attendance Card */}
        {currentAttendance && (
          <div
            style={{
              background: '#d3d3d3',
              border: '1px solid #c0c0c0',
              borderRadius: '8px',
              padding: '2rem',
              transition: 'all 300ms ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
              <div>
                <h3 style={{ fontWeight: 700, margin: '0 0 1rem 0', color: '#1a1a1a', fontSize: '1.25rem' }}>
                  {currentAttendance.title}
                </h3>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <span style={{
                    background: currentAttendance.status === 'open' ? '#e8f5e9' : '#ffebee',
                    color: currentAttendance.status === 'open' ? '#2e7d32' : '#c62828',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}>
                    {currentAttendance.status === 'open' ? '● Open' : '● Closed'}
                  </span>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#1a1a1a' }}>
                    Google Form URL:
                  </label>
                  <p style={{ color: '#666', margin: '0', wordBreak: 'break-all', fontSize: '0.9rem' }}>
                    {currentAttendance.formUrl}
                  </p>
                </div>

                <a
                  href={currentAttendance.formUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#1a1a1a',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '0.75rem 1.5rem',
                    background: '#e8e8e8',
                    borderRadius: '6px',
                    transition: 'all 300ms ease',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#d8d8d8';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#e8e8e8';
                  }}
                >
                  <i className="fas fa-external-link-alt"></i>
                  Open Form in New Tab
                </a>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', minWidth: '150px' }}>
                <button
                  onClick={() => toggleAttendanceStatus(currentAttendance.id)}
                  disabled={savingSessionId === currentAttendance.id}
                  style={{
                    background: savingSessionId === currentAttendance.id ? '#dcdcdc' : (currentAttendance.status === 'open' ? '#e8e8e8' : '#1a1a1a'),
                    color: savingSessionId === currentAttendance.id ? '#999' : (currentAttendance.status === 'open' ? '#1a1a1a' : 'white'),
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: savingSessionId === currentAttendance.id ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    transition: 'all 300ms ease',
                    opacity: savingSessionId === currentAttendance.id ? 0.7 : 1
                  }}
                  onMouseOver={(e) => {
                    if (savingSessionId !== currentAttendance.id) {
                      e.target.style.opacity = '0.9';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (savingSessionId !== currentAttendance.id) {
                      e.target.style.opacity = '1';
                    }
                  }}
                >
                  {savingSessionId === currentAttendance.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                      Saving...
                    </>
                  ) : (
                    currentAttendance.status === 'open' ? 'Close' : 'Open'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Weeks Overview */}
        <div style={{ marginTop: '3rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem', color: '#1a1a1a', paddingBottom: '1rem', borderBottom: '2px solid #e8e8e8' }}>
            All Weeks Overview
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {attendanceSessions.map(session => (
              <div
                key={session.id}
                style={{
                  background: '#d3d3d3',
                  border: '1px solid #c0c0c0',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  transition: 'all 300ms ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = '#1a1a1a';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                }}
                onClick={() => setCurrentWeek(session.week)}
              >
                <h3 style={{ fontWeight: 700, margin: '0 0 1rem 0', color: '#1a1a1a', fontSize: '1.1rem' }}>
                  Week {session.week}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{
                    background: session.status === 'open' ? '#e8f5e9' : '#ffebee',
                    color: session.status === 'open' ? '#2e7d32' : '#c62828',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }}>
                    {session.status === 'open' ? '● Open' : '● Closed'}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAttendanceStatus(session.id);
                    }}
                    disabled={savingSessionId === session.id}
                    style={{
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 600,
                      cursor: savingSessionId === session.id ? 'not-allowed' : 'pointer',
                      background: savingSessionId === session.id ? '#dcdcdc' : (session.status === 'open' ? '#e8e8e8' : '#1a1a1a'),
                      color: savingSessionId === session.id ? '#999' : (session.status === 'open' ? '#1a1a1a' : 'white'),
                      transition: 'all 300ms ease',
                      opacity: savingSessionId === session.id ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (savingSessionId !== session.id) {
                        e.target.style.opacity = '0.9';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (savingSessionId !== session.id) {
                        e.target.style.opacity = '1';
                      }
                    }}
                  >
                    {savingSessionId === session.id ? (
                      <>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.3rem' }}></i>
                        Saving...
                      </>
                    ) : (
                      session.status === 'open' ? 'Close' : 'Open'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
